import { Heading } from "@elaniintc/goes-design-system";
import SectionCard from "@/components/ui/section-card";
import { useNavigate } from "react-router";
import { useListFaqs } from "@/queries/faq/faq.api";
import dayjs from "dayjs";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { useGetSuspensionConfigs } from "@/queries/deadline-suspensions/deadline-suspensions.api";
import { NpePaymentsModal } from "@/components/portal/npe-payments-modal";
import { useState } from "react";

export const PortalPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuthorization();
  const [isNpeModalOpen, setIsNpeModalOpen] = useState(false);
  const canReadFaqs = hasPermission(Permissions.faqs.read);
  const canReadAnnouncements = hasPermission(Permissions.announcements.read);
  const canConfiguredeadlines = hasPermission(
    Permissions["node-deadline-suspensions-config"].read,
  );

  const { data: listFaqs, isLoading } = useListFaqs(canReadFaqs);
  const { data: configs, isLoading: isLoadingConfigs } =
    useGetSuspensionConfigs(canConfiguredeadlines);

  const sections = [
    ...(canReadFaqs
      ? [
          {
            title: "Preguntas Frecuentes",
            description:
              "Ayuda sobre el funcionamiento del portal y sus trámites.",
            lastUpdated: listFaqs?.data[0]?.updated_at
              ? dayjs(listFaqs.data[0].updated_at).format(
                  "MMM DD, YYYY | HH:mm A",
                )
              : undefined,
            iconName: "seal-question",
            iconType: "fas" as const,
            path: "/portal/preguntas-frecuentes",
          },
        ]
      : []),
    ...(canReadAnnouncements
      ? [
          {
            title: "Notificaciones",
            description:
              "Avisos generales para los usuarios regulados del portal.",
            iconName: "message-exclamation",
            iconType: "fas" as const,
            path: "/portal/notifications",
          },
        ]
      : []),
    ...(canConfiguredeadlines
      ? [
          {
            title: "Plazos de suspensión",
            description:
              "Plazos máximos de suspensión de trámites que pueden solicitar los tramitadores.",
            iconName: "circle-stop",
            iconType: "fas" as const,
            path: "/portal/plazos-de-suspension",
            lastUpdated: configs?.last_bulk_operation?.performed_at
              ? dayjs(configs.last_bulk_operation.performed_at).format(
                  "MMM DD, YYYY | HH:mm A",
                )
              : undefined,
          },
        ]
      : []),
    {
      title: "Pagos con NPE",
      description:
        "Actualiza el registro de los pagos efectuados mediante NPE en tu institución",
      lastUpdated: "Mar, 2024 | 19:24 PM",
      iconName: "money-check-dollar",
      iconType: "fas" as const,
      onClick: () => setIsNpeModalOpen(true),
    },
  ];

  return (
    <>
      <div className="p-4">
        <div className="space-y-6">
          <div className="space-y-3">
            <Heading variant="h4">Configuraciones del portal</Heading>
            <p className="text-body">
              Administra la información que se muestra públicamente en el portal
              de trámites de esta institución.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 w-full md:w-fit">
            {sections.map((section, index) => (
              <SectionCard
                key={index}
                title={section.title}
                description={section.description}
                lastUpdated={section.lastUpdated}
                iconName={section.iconName}
                iconType={section.iconType}
                onClick={
                  section.onClick
                    ? section.onClick
                    : () => navigate(section.path!)
                }
                isLoadingDate={isLoading || isLoadingConfigs}
              />
            ))}
          </div>
        </div>
      </div>

      <NpePaymentsModal
        isOpen={isNpeModalOpen}
        onClose={() => setIsNpeModalOpen(false)}
      />
    </>
  );
};
